<script setup lang="ts">
import round from 'lodash/round'

import { loadGoogleAPI } from '~/lib/clients/googleMaps'
import { distanceBetweenCoordinates } from '~/lib/geoUtils'
import { RestaurantLiteWithDistanceList } from '~/lib/interfaces/restaurant'
import { Coords } from '~/lib/models/geolocation'
import { GTMContentGroup } from '~/lib/models/gtm'

const props = defineProps<{ cityName?: string }>()

const { t } = useLocale()
const countryCode = useCountry()
const { middlewareServiceRef } = useStoreService()
const middlewareService = middlewareServiceRef()

const { pending, data: restaurants } = await useWrapperAsyncData(
  () => middlewareService.fetchRestauarnList(countryCode),
  { lazy: true }
)

const localization = useConfiguration('localization')
const { navigateToHome, navigateToRestaurants } = useMcdRoutes()
const authStore = useAuthStore()
const appStore = useAppStore()
const restaurantStore = useRestaurantStore()
const { trackPageView } = useGTM()

const isMobile = inject<Ref<boolean>>('isMobile')

const isGoogleLoaded = ref(false)
const searchQuery = ref(false)
const restaurantList = ref<RestaurantLiteWithDistanceList>()
const initialCenter = ref<Coords>()

const placeCenter = computed(() => {
  const lat = searchQuery.value
    ? restaurantStore.selectedGoogleLocation?.lat
    : initialCenter.value?.lat || localization?.latitude || 0
  const lng = searchQuery.value
    ? restaurantStore.selectedGoogleLocation?.lng
    : initialCenter.value?.lng || localization?.longitude || 0
  return {
    lat,
    lng,
  }
})

const loopThroughRestaurants = () => {
  if (!restaurants.value) return []
  return restaurants.value.reduce((acc, res) => {
    if (props.cityName && removeAccents(res.city).toLowerCase() !== props.cityName) {
      return acc
    }

    const isFavorite = appStore.isEcommerceDisabled
      ? false
      : isFavoriteRestaurant({
          countryCode,
          favoriteRestaurantsMetadata: authStore?.userMetadata?.favoriteRestaurants,
          restaurantCode: res.code,
        })

    return [
      ...acc,
      {
        ...res,
        isFavorite,
        distance: round(
          distanceBetweenCoordinates(
            placeCenter.value.lat,
            placeCenter.value.lng,
            res.coordinates.latitude,
            res.coordinates.longitude,
            'K'
          ),
          2
        ),
      },
    ]
  }, [] as RestaurantLiteWithDistanceList)
}

watch(
  placeCenter,
  () => {
    restaurantList.value = loopThroughRestaurants()
  },
  { deep: true }
)

watch(
  pending,
  () => {
    if (!pending.value) {
      restaurantList.value = loopThroughRestaurants()
    }
  },
  { immediate: true }
)

const handleOnBack = async () => {
  if (props.cityName) {
    await navigateToRestaurants()
  } else {
    await navigateToHome()
  }
}

onMounted(async () => {
  if (process.client) {
    const apiKey = getOverridenGoogleMapsKey() || localization?.googleMapsKey
    await loadGoogleAPI({ apiKey, region: localization?.country })
    const defaultInitialCenter = {
      lat: localization?.latitude || 0,
      lng: localization?.longitude || 0,
    }
    if (!appStore.isEcommerceDisabled) {
      initialCenter.value =
        (await getUserLocationForRestaurants({
          cityName: props.cityName,
          countryCode,
        })) || defaultInitialCenter
    } else {
      initialCenter.value = defaultInitialCenter
    }

    isGoogleLoaded.value = true
  }
  const viewContent = {
    cg: GTMContentGroup.primary.OUR_MENU,
    cg2: GTMContentGroup.secondary.LOCATOR,
  }
  trackPageView(viewContent)
})
</script>

<template>
  <main>
    <template v-if="isMobile">
      <SkeletonRestaurantViewMobile v-if="!(isGoogleLoaded && placeCenter)" />
      <div v-else>
        <h1 class="collapse h-0 w-0">{{ t('selectRestaurant.MOP.findYourMcDonalds') }}</h1>
        <MOPModal
          :ecommerce="false"
          :mdw-restuarant-list="restaurantList ?? []"
          :map-center="placeCenter"
          on-back
          @back="handleOnBack"
        />
      </div>
    </template>
    <template v-else>
      <div class="mx-auto mt-20 w-fit">
        <div class="my-8 flex h-[calc(100vh-(170px))] w-full justify-center gap-24">
          <div>
            <h1 class="mb-5 w-[376px] text-[32px] font-bold leading-9 tracking-[-0.15px]">
              {{ t('selectRestaurant.MOP.findYourMcDonalds') }}
            </h1>
            <div
              class="mcd-custom-scrollbar flex h-[calc(100%-(56px))] w-[376px] flex-col overflow-x-hidden overflow-y-scroll pr-8"
            >
              <RestaurantList
                :restaurant-list="restaurantList ?? []"
                :city-slug="cityName"
                :small-card="true"
              />
            </div>
          </div>

          <div class="w-[600px] gap-4">
            <SkeletonRestaurantViewDesktop v-if="!(isGoogleLoaded && placeCenter)" />
            <div v-else>
              <div class="mb-6 w-[600px]">
                <SearchBar
                  v-if="!cityName"
                  :restaurant-list="true"
                  absolute-places
                  @on-search="searchQuery = true"
                />
              </div>
              <div class="h-[calc(100vh-(226px))] overflow-hidden rounded-md">
                <MapGoogle :restaurant-list="restaurantList ?? []" :center-map="placeCenter" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </main>
</template>
